import React, { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import useListenPaste from "../../../hooks/useListenPaste";

interface OTPInputBoxProps {
  errorMessage?: string;
  resetErrorMessage: () => void;
  setCompleteOtp: (otp: string) => void;
  errorTextClassName?: string;
  resetCount: number;
}

const OTPInputBox: React.FC<OTPInputBoxProps> = ({ errorMessage = "", resetErrorMessage, setCompleteOtp, errorTextClassName = "", resetCount }) => {
  const [otp, setOtp] = useState<string[]>(Array(6).fill(""));
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useListenPaste({
    textLength: 6,
    onPaste: (otp: string) => {
      setOtp(otp.split("").slice(0, 6));
      if (errorMessage) resetErrorMessage();
    },
  });

  useEffect(() => {
    const OTP = otp.join("");
    if (OTP.length === 6) {
      setCompleteOtp(OTP);
    } else {
      setCompleteOtp("");
    }
  }, [otp]);

  useEffect(() => {
    if (resetCount > 0) {
      setOtp(Array(6).fill(""));
    }
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, [resetCount]);

  const handleChange = (index: number, value: string) => {
    // Handle mobile keyboard paste - when user pastes 6 digits into first input
    if (index === 0 && value.length === 6) {
      // Check if all characters are numbers
      if (/^\d{6}$/.test(value)) {
        const otpArray = value.split("");
        setOtp(otpArray);
        if (errorMessage) resetErrorMessage();
        // Focus on the last input after paste
        inputRefs.current[5]?.focus();
        return;
      }
    }

    if (isNaN(Number(value))) return;
    if (errorMessage) resetErrorMessage();

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value !== "" && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && index > 0 && otp[index] === "") {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const inputVariants = {
    focus: { scale: 1.05, borderColor: "#3B82F6" },
    blur: { scale: 1, borderColor: "#D1D5DB" },
  };

  // useEffect(() => {
  //   if (errorMessage) {
  //     setTimeout(() => setOtp(Array(6).fill("")), 1000);
  //   }
  // }, [errorMessage]);

  return (
    <div className="flex flex-col items-center">
      <div className="flex justify-center items-center space-x-4 w-full">
        {otp.map((digit, index) => (
          <motion.input
            key={index}
            type="tel"
            maxLength={1}
            value={digit}
            onChange={(e) => handleChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            ref={(el) => (inputRefs.current[index] = el)}
            className={`w-[36px] h-[40px] text-13 border text-center rounded focus:outline-none bg-stroke-light/10 text-black font-bold ${
              errorMessage ? "!border-text-error" : ""
            }`}
            variants={inputVariants}
            initial="blur"
            whileFocus="focus"
            animate={digit ? "focus" : "blur"}
            transition={{ type: "spring", stiffness: 300, damping: 20 }}
          />
        ))}
      </div>
      {errorMessage && (
        <motion.p
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className={`text-sm w-full text-start text-text-error mt-2 ${errorTextClassName}`}
        >
          {errorMessage}
        </motion.p>
      )}
    </div>
  );
};

export default OTPInputBox;
